import React from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/react-hooks';
import {
  Box,
  Flex,
  Heading,
  Link,
  Spinner,
  Text,
} from '@chakra-ui/react';

import { Provider } from '../../Chakra';
import { EVENT_TITLE } from '../../Constants/AppConstants';
import { EXPORT_SCORE_SUMMARY } from '../../GraphQL/Queries';
import useHeaderComponentsMutation from '../../Hooks/useHeaderComponentsMutation';
import useCurrentUser from '../../Hooks/userCurrentUser';

const MemberPropType = PropTypes.shape({
  code: PropTypes.number.isRequired,
  firstname: PropTypes.string.isRequired,
  lastname: PropTypes.string.isRequired,
  school: PropTypes.string.isRequired,
  region: PropTypes.string,
});

const TeamPropType = PropTypes.shape({
  code: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  members: PropTypes.arrayOf(MemberPropType).isRequired,
});

const CoalitionPropType = PropTypes.shape({
  code: PropTypes.number.isRequired,
  teams: PropTypes.arrayOf(TeamPropType).isRequired,
});

const WinnerPropType = PropTypes.shape({
  code: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
});
const CompetitionPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  winners: PropTypes.arrayOf(WinnerPropType).isRequired,
});

const CategoryPropType = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  competitions: PropTypes.arrayOf(CompetitionPropType).isRequired,
});

const MemberWinnerDetail = ({ member, showSchool }) => (
  <li>
    {member.firstname}
    {' '}
    {member.lastname}
    {showSchool && (
      <>
        {' ('}
        {member.school}
        {')'}
      </>
    )}
    {member.region && (
      <>
        {' ('}
        {member.region}
        {')'}
      </>
    )}
  </li>
);
MemberWinnerDetail.propTypes = {
  member: MemberPropType.isRequired,
  showSchool: PropTypes.bool,
};
MemberWinnerDetail.defaultProps = {
  showSchool: false,
};

const TeamWinnerDetail = ({ team }) => (
  <li>
    <div>
      {team.name}
      {team.region && (
        <>
          {' ('}
          {team.region}
          {')'}
        </>
      )}
      <div style={{ marginLeft: '16px' }}>
        <ul>
          {team.members.map((member) => <MemberWinnerDetail key={member.code} member={member} />)}
        </ul>
      </div>
    </div>
  </li>
);
TeamWinnerDetail.propTypes = {
  team: TeamPropType.isRequired,
};

const CoalitionWinnerDetail = ({ coalition }) => (
  <li>
    <div>
      Coalition
      {' '}
      {coalition.code}
      <div style={{ marginLeft: '16px' }}>
        <ul>
          {coalition.teams.map((team) => <TeamWinnerDetail key={team.code} team={team} />)}
        </ul>
      </div>
    </div>
  </li>
);
CoalitionWinnerDetail.propTypes = {
  coalition: CoalitionPropType.isRequired,
};

const WinnerDetail = ({ winner }) => {
  if (winner.type === 'Member') {
    return <MemberWinnerDetail member={winner} showSchool />;
  }
  if (winner.type === 'Team') {
    return <TeamWinnerDetail team={winner} />;
  }
  if (winner.type === 'Coalition') {
    return <CoalitionWinnerDetail coalition={winner} />;
  }
  return null;
};
WinnerDetail.propTypes = WinnerPropType.isRequired;

const CompetitionWinners = ({ competition }) => (
  <Flex direction="column" marginBlock={2}>
    <Heading as="h4" size="sm">
      {competition.name}
    </Heading>
    <ol>
      {competition.winners.map((w) => <WinnerDetail key={`${w.type}-${w.code}`} winner={w} />)}
    </ol>
  </Flex>
);
CompetitionWinners.propTypes = {
  competition: CompetitionPropType.isRequired,
};

const CategoryWinners = ({ category }) => (
  <Flex direction="column" paddingBottom={4}>
    <Heading as="h3" size="md">
      {category.name}
    </Heading>
    {category.competitions.map((competition) => (
      <CompetitionWinners key={competition.id} competition={competition} />
    ))}
  </Flex>
);
CategoryWinners.propTypes = {
  category: CategoryPropType.isRequired,
};

const EVENT_DETAIL = gql`
  query eventDetail($eventId: Int!) {
    fetchEventDetail(id: $eventId) {
      id
      title
      ballotsFinalized
    }
  }
`;

const EventWinners = () => {
  const {
    currentUser,
    loading: userLoading,
  } = useCurrentUser(true);
  const { eventId } = useParams();

  const { data: eventData, loading: eventLoading } = useQuery(EVENT_DETAIL, {
    variables: {
      eventId,
    },
  });

  useHeaderComponentsMutation({
    title: 'EVENT WINNERS',
    backLink: '/events',
    components: [{ key: EVENT_TITLE, value: eventData?.fetchEventDetail.title }],
  });

  const { data: winnersData, loading: winnersLoading, error } = useQuery(EXPORT_SCORE_SUMMARY, {
    variables: {
      eventId,
      format: 'json',
    },
    skip: !currentUser || !eventId || !eventData?.fetchEventDetail.ballotsFinalized,
  });

  if (userLoading || winnersLoading || eventLoading) {
    return (
      <Flex direction="column" justifyContent="center" alignItems="center" height="100%" padding="72px">
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (!eventData?.fetchEventDetail.ballotsFinalized) {
    return (
      <Flex direction="column" justifyContent="center" alignItems="center" height="100%" padding="72px">
        <Text>
          The scores have not yet been released for this event. Please check again later.
        </Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" justifyContent="center" alignItems="center" height="100%" padding="72px">
        <Text>
          An error occurred. Please check again later.
        </Text>
      </Flex>
    );
  }

  const winners = winnersData.exportScore.content;

  return (
    <Flex direction="column" paddingInline={8} paddingBlock={4}>
      <Text paddingBlock={4}>
        Congratulations to all of the winners of the event!
        Below are the winners for each competition, as well as the overall winners.
        In most cases, the top 5 competitors from each category qualify for the National Tournament.
        Some exceptions apply.
        {' '}
        <Link href="https://www.getmistified.com/" target="_blank" rel="noopener noreferrer">
        Click here to see more details on our website.
        </Link>
      </Text>
      <Box paddingBottom={4}>
        <Heading as="h2">
          Winners
        </Heading>
        {winners.categories.map((category) => (
          <CategoryWinners key={category.id} category={category} />
        ))}
      </Box>

      <Box paddingBottom={4}>
        <Heading as="h2">
          Overall Students
        </Heading>
        <ol>
          {winners.overallStudents.map((student) => (
            <li key={student}>{student}</li>
          ))}
        </ol>
      </Box>

      <Box paddingBottom={4}>
        <Heading as="h2">
          Overall Schools
        </Heading>
        <ol>
          {winners.overallSchools.map((school) => (
            <li key={school}>{school}</li>
          ))}
        </ol>
      </Box>

      {winners.overallRegions?.length > 0 && (
        <Box paddingBottom={4}>
          <Heading as="h2">
            Overall Regions
          </Heading>
          <ol>
            {winners.overallRegions.map((region) => (
              <li key={region}>{region}</li>
            ))}
          </ol>
        </Box>
      )}
    </Flex>
  );
};

export default () => (
  <Provider>
    <EventWinners />
  </Provider>
);
